import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import SplitLoginLayout, {
  Form,
  Field,
  Label,
  Input,
  PasswordWrap,
  PasswordInput,
  EyeButton,
  EyeOpenIcon,
  EyeOffIcon,
  OptionsRow,
  Remember,
  ForgotLink,
  SubmitButton,
  ErrorText,
} from '@medical/shared/components/SplitLoginLayout';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { useHospitalBranding } from '@medical/shared/utils/hospitalBranding';

const AdminLogin = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const branding = useHospitalBranding(formData.identifier);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const identifier = formData.identifier.trim();
    if (!identifier) {
      setError('Please enter your Admin ID');
      return;
    }
    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/auth/admin/login', {
        identifier: identifier.includes('@') ? identifier.toLowerCase() : identifier.toUpperCase(),
        password: formData.password,
        rememberMe: formData.rememberMe
      });

      const { token, refreshToken, user } = response.data;

      localStorage.setItem('adminToken', token);
      if (formData.rememberMe && refreshToken) {
        localStorage.setItem('adminRefreshToken', refreshToken);
      }
      localStorage.setItem('adminUser', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      toast.success(`Welcome, ${user.firstName}!`);
      window.dispatchEvent(new Event('adminLogin'));

      if (onLoginSuccess) {
        onLoginSuccess(user, token);
      }
    } catch (err) {
      console.error('Admin login error:', err);
      const message = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitLoginLayout
      title="Hospital Admin Login"
      subtitle="Manage doctors, front desk staff and patients for your hospital."
      badgeLabel="ADMINISTRATION"
      artSrc="/login-admin.jpg"
      artAlt="Hospital administrator reviewing a dashboard"
      artCaption="Everything your hospital team needs, in one secure place."
      brandExtra={<HospitalLogo logoUrl={branding?.logoUrl} name={branding?.name} />}
    >
      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="identifier">Admin ID / Email</Label>
          <Input
            type="text"
            id="identifier"
            name="identifier"
            value={formData.identifier}
            onChange={handleInputChange}
            placeholder="Enter Admin ID or email"
            required
            autoFocus
            autoComplete="username"
          />
        </Field>

        <Field>
          <Label htmlFor="password">Password</Label>
          <PasswordWrap>
            <PasswordInput
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <EyeButton
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
            </EyeButton>
          </PasswordWrap>
        </Field>

        <OptionsRow>
          <Remember htmlFor="rememberMe">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
            />
            Remember me for 90 days
          </Remember>
          <ForgotLink
            type="button"
            onClick={() => toast('Please contact the platform team (Super Admin) to reset your password.')}
          >
            Forgot Password?
          </ForgotLink>
        </OptionsRow>

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? 'Please wait...' : 'Login'}
        </SubmitButton>
      </Form>
    </SplitLoginLayout>
  );
};

export default AdminLogin;
